import { yupResolver } from '@hookform/resolvers/yup';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextareaAutosize from '@material-ui/core/TextareaAutosize';
import Typography from '@material-ui/core/Typography';
import CameraAltOutlinedIcon from '@material-ui/icons/CameraAltOutlined';
import CloseIcon from '@material-ui/icons/Close';
import Box from '@mui/material/Box';
import clsx from 'clsx';
import ActionButton from 'components-v2/atoms/ActionButton';
import { schemaReply } from 'components-v2/organisms/QuestionAndAnswer/constants';
import { useUploadSingleFile } from 'hooks/useUploadSingleFile';
import Image from 'next/image';
import { ChangeEvent, CSSProperties, useRef, useState } from 'react';
import { Controller, get, useForm } from 'react-hook-form';
import { getLinkCDNImage } from 'utils/ImageUtils';
import NotifyUtils from 'utils/NotifyUtils';
import styles from './styles.module.css';

export interface ReplyData {
  reply: string;
  images?: string[];
}

interface Props {
  className?: string;
  type?: string;
  image: string;
  replyName: string;
  style?: CSSProperties;
  onSubmit: (data: ReplyData) => void;
}

const MAX_IMAGES = 3;
const MAX_SIZE_MB = 4;
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const ACCEPTED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp'];

const CommentForm = (props: Props) => {
  const { className, type, image, replyName, style, onSubmit } = props;
  const [images, setImages] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { onChange: uploadOnChange, isLoading: isUploading } = useUploadSingleFile({
    maxFileSize: MAX_SIZE_MB,
    callback: async (url: string) => {
      if (url) {
        setImages((prev) => (prev.length < MAX_IMAGES ? [...prev, url] : prev));
        return true;
      }
      return false;
    },
  });

  const { control, handleSubmit, formState } = useForm({
    resolver: yupResolver(schemaReply),
  });

  const handleOpenFilePicker = () => {
    if (images.length >= MAX_IMAGES || isUploading) return;
    fileInputRef.current?.click();
  };

  const handleSelectFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      if (!ACCEPTED_TYPES.includes(file.type) || !ACCEPTED_EXTENSIONS.includes(extension)) {
        NotifyUtils.error('Chỉ hỗ trợ JPG, JPEG, PNG, WEBP');
        e.target.value = '';
        return;
      }
    }
    uploadOnChange(e);
    e.target.value = '';
  };

  const handleRemoveImage = (url: string) => {
    setImages((prev) => prev.filter((img) => img !== url));
  };

  const handleFormSubmit = handleSubmit((data) => {
    onSubmit({ reply: (data as ReplyData).reply, images });
    setImages([]);
  });

  const canAddMore = images.length < MAX_IMAGES && !isUploading;

  return (
    <Box className={clsx(className && className, styles.replyInput, { [styles[`replyInput-type-${type}`]]: !!type })} style={style}>
      <Box className={styles.replyAvatar}>
        <Image className={styles.replyAvatarImg} src={image} width={24} height={24} loading="lazy" alt="avatar reply" />
      </Box>
      <Box className={styles.replyInfo}>
        <Typography variant="h4" className={styles.replyName}>
          Đang phản hồi: <span>{replyName}</span>
        </Typography>
        <form className={styles.replyForm}>
          <Box className={styles.replyFormInput}>
            <Controller
              name="reply"
              control={control}
              render={({ field }) => {
                const error = get(formState.errors, field.name);
                return (
                  <Box className={styles.formTextarea}>
                    <Box className={clsx(styles.commentBox, { [styles.commentBoxError]: !!error })}>
                      <TextareaAutosize
                        className={styles.input}
                        minRows={2}
                        maxRows={2}
                        maxLength={200}
                        placeholder="* Vui lòng nhập nội dung phản hồi bằng tiếng Việt có dấu, tối thiểu 3 ký tự (Bắt buộc)"
                        {...field}
                      />
                      <Box className={styles.commentBoxToolbar}>
                        <Box
                          role="button"
                          className={clsx(styles.replyUploadBtn, { [styles.replyUploadBtnDisabled]: !canAddMore })}
                          onClick={canAddMore ? handleOpenFilePicker : undefined}
                        >
                          {isUploading ? (
                            <CircularProgress color="inherit" size={16} thickness={4} style={{ color: 'var(--primary-color)' }} />
                          ) : (
                            <CameraAltOutlinedIcon fontSize="small" />
                          )}
                          <span>
                            {isUploading ? 'Đang tải ảnh lên...' : `Thêm ảnh (${images.length}/${MAX_IMAGES})`}
                          </span>
                        </Box>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept={ACCEPTED_TYPES.join(',')}
                          hidden
                          onChange={handleSelectFile}
                        />
                        {error ? <span className={styles.commentBoxError}>{error.message}</span> : <div className={styles.textareaCount}>{field.value ? field.value.length : 0}/ 200</div>}
                      </Box>
                      {images.length > 0 && (
                        <Box className={styles.thumbnails}>
                          {images.map((url) => (
                            <Box key={url} className={styles.thumbnailItem}>
                              <Image src={getLinkCDNImage(url)} width={56} height={56} className={styles.thumbnailImage} />
                              <Box
                                className={styles.thumbnailRemove}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveImage(url);
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              }}
            />
          </Box>
          <Box className={styles.submit}>
            <ActionButton variant="contained" color="secondary" disabled={isUploading} onClick={handleFormSubmit}>
              Gửi phản hồi
            </ActionButton>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default CommentForm;
